import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxGl from 'mapbox-gl';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Button, Loading } from '@carbon/react';
import type { Geometry } from 'geojson';
import TypeCurveMap from 'pages/type-curves/type-curve-map';
import { TcSlotGeometriesLayer } from 'pages/type-curves/tc-slot-geometries-layer';
import {
  builderSurroundingsAtom,
  mapSlotGeomAtoms,
} from 'pages/type-curves/inventory-surface/atoms';
import type { Pieces } from 'components/geometry-editing';
import { tcSelectedDevAreaAtom } from 'pages/type-curves/type-curve-filters/atoms';
import { mapRefAtoms } from 'pages/type-curves/inventory-surface/map-atoms';
import { SurveyAreasLayer, MapPickControl } from 'components/query-map';
import { typeCurveUnitsVisibilityAtom } from 'pages/type-curves/atoms';
import { UnitBuilderDevAreaLayers } from './unit-builder-dev-area-layers';
import {
  useSurveyAreaPick,
  describeSurveyAreaCandidate,
} from 'components/survey-description';
import { useMapZoomBelow } from 'components/map';
import { useAreaPreview } from 'components/create-area-panel';
import { composeUnitBoundary, piecesToGeojson } from './compose-boundary';
import { useUnitBuilder } from './context';
import { useInventoryUnitRotation } from './use-inventory-unit-rotation';
import {
  reslotRequestAtom,
  unitRegeneratingAtom,
  surveyRowsAtom,
  builderSelectedFormationAtom,
  builderUnitNameAtom,
  builderNameCheckAtom,
  boundaryUserEditedAtom,
} from './atoms';
import {
  outlineRingsFor,
  outlineVerticesFor,
  quarterGridForUnit,
} from './quarter-grid';
import { useHydrateSurveyGeometries } from './use-hydrate-survey-geometries';
import { useCloneUnit } from './use-clone-unit';
import { QuarterGridLayer } from './quarter-grid-layer';
import { TcLateralTrimLayer } from './tc-lateral-trim-layer';
import { MergeGeometriesLayer } from './merge-geometries-layer';
import { TcSetbackRingLayer } from './tc-setback-ring-layer';
import { UnitBuilderPaneHeader } from './unit-builder-pane-header';
import { UnitBuilderMapToolbar } from './unit-builder-map-toolbar';
import { UnitBuilderRowResizer } from './unit-builder-row-resizer';
import { useSurveyRowEdit } from './use-survey-row-edit';
import { UnitBuilderSurveyAreas } from './unit-builder-survey-areas';
import style from './unit-builder-modal.module.scss';

/**
 * Outer ring per piece of a composed boundary geometry, as the shape editor's
 * `Pieces` — holes are dropped (units don't carry them).
 *
 * @param {Geometry} geom - The previewed survey-union geometry.
 * @return {Pieces} One `[lng,lat]` ring per polygon piece.
 */
// One shared empty boundary. Identity matters: with no tract picked, the preview
// runs on the DRAWN boundary, so a fresh `[]` here would give `composedPieces` a
// new identity every time that preview settled — re-firing the compose effect,
// which rewrites the pieces, which re-runs the drawn preview.
const NO_PIECES: Pieces = [];

const geometryToPieces = (geom: Geometry): Pieces => {
  if (geom.type === 'Polygon') return [geom.coordinates[0]];
  if (geom.type === 'MultiPolygon') return geom.coordinates.map((p) => p[0]);
  return [];
};

interface UnitBuilderMapProps {
  /** Route identifier the map resolves its type curve by. */
  typeCurveId: string | undefined;
}

/**
 * The surrounding development units, from `TcDevelopmentAreasLayer` — the base
 * teal set plus the colour-by set. The mock's Surroundings toggle hides exactly
 * these: faint reference shapes around the unit being edited.
 *
 * Deliberately excludes `tc-dev-area-selected-*` and `tc-dev-area-overlap-*`:
 * those draw the scoped unit and its overlaps, so hiding them would take away
 * the unit under edit.
 */

/**
 * The survey grid tiles carry no section polygons below this zoom, so picking is
 * dead until the map is at least this close — arming zooms in to here, and the
 * hint asks the user to zoom in if they pull back out.
 */
const SURVEY_PICK_ZOOM = 12;

/**
 * The builder's edit map: the workspace map in unit-shape-edit mode, driven by
 * the boundary the shell owns.
 *
 * Rendered under the `'builder'` surface, so its `TypeCurveMap` and layers read
 * the builder's OWN map atoms (`mapRefAtoms.builder`, filter/AOI/working-ring) —
 * fully independent of the Main Page map, which stays mounted behind the modal.
 *
 * @param {UnitBuilderMapProps} props - Route id and snapping preference.
 * @return {JSX.Element} The edit map.
 */
export const UnitBuilderMap: FC<UnitBuilderMapProps> = ({ typeCurveId }) => {
  // Clone unit (frontend-only, mock parity): owned here because it needs the
  // builder's live shape edit + survey rows, which live in this subtree.
  const handleCloneUnit = useCloneUnit();
  const { shapeEdit, originalRing, baseRing, unitId, inventoryWorkspaceId } =
    useUnitBuilder();
  const selectedDevArea = useAtomValue(tcSelectedDevAreaAtom);
  // The builder's arrow-stepped band, so this map's slot fetch follows it —
  // decoupled from the main page's `tcHighlightedFormationAtom`.
  const builderFormation = useAtomValue(builderSelectedFormationAtom);
  // Azimuth defines the section line every formation is slotted against, so a
  // rotate invalidates the layout in all of them. The server re-lays the whole
  // unit — every formation package — asynchronously and reports it in the rotate
  // response, which the hook hands to the shared regeneration tally (banner +
  // progress in the modal and the main page). Only when the server started
  // nothing does this fall back to the gun barrel pane's own single-formation
  // re-slot; doing both would generate the on-screen formation twice.
  const requestReslot = useSetAtom(reslotRequestAtom);
  // The last rotate's regeneration is still running — the toolbar shows it and
  // holds the rotate button until every formation has reported.
  const regenerating = useAtomValue(unitRegeneratingAtom);
  const rotation = useInventoryUnitRotation(
    inventoryWorkspaceId,
    unitId,
    selectedDevArea?.azimuthDeg ?? null,
    // The map layer swings the section line itself, onto the bearing the server
    // states in the response; the unit's boundary stays where it was drawn.
    (regeneration) => {
      if (!regeneration) requestReslot((n) => n + 1);
    }
  );
  // The builder map's OWN ref — the Main Page map keeps its own, so the two never
  // fight over one shared ref (they are both mounted at once).
  const mapRef = useAtomValue(mapRefAtoms.builder);

  // An unsaved draft — a clone or a brand-new unit. Only a draft may compose its
  // boundary from surveys or be renamed; an EXISTING unit's survey description is
  // already persisted, so it is shown but never edited from here.
  const isDraftUnit = unitId === null;
  // The name being typed in the header, sent with the preview so the dry run
  // reports whether it is already taken (drafts only — see the header).
  const draftName = useAtomValue(builderUnitNameAtom);
  const effectiveName = (
    draftName ??
    selectedDevArea?.developmentAreaName ??
    ''
  ).trim();

  // The map's LTD slot geometries refetch whenever the scoped formation band
  // changes (the gun barrel's ↑/↓ arrows) — as well as on open / generate — so
  // show a loading overlay on the map while that fetch is in flight. Only a real
  // unit fetches these (the layer is unmounted for a draft).
  const mapSlots = useAtomValue(mapSlotGeomAtoms.builder);
  const mapSlotsLoading = unitId !== null && mapSlots.loading;

  // A real unit edits its true server polygon and nothing else, so until that
  // read lands there is no boundary to draw. Say so, rather than showing a map
  // with no editable shape on it and looking broken.
  const boundaryLoading =
    unitId !== null && (originalRing === null || originalRing.length === 0);

  // All three default on, as the mock's do: the units and grid are already drawn
  // when the builder opens, and these only hide them.
  const [snap, setSnap] = useState(true);
  const [grid, setGrid] = useState(true);
  // Shared, not local: `TcDevelopmentAreasLayer` owns the dev-area layers and
  // composes this with the map panel's "Type Curve → Units" checkbox, so their
  // visibility has one writer.
  const [surroundings, setSurroundings] = useAtom(builderSurroundingsAtom);
  const unitsVisible = useAtomValue(typeCurveUnitsVisibilityAtom);

  // One dataset, two consumers — as in the mock: the dots the grid toggle draws
  // are exactly the points a dragged vertex snaps to, a 5×5 grid per survey
  // tract. Shared with the survey panel via the atom, so a map pick and a
  // dropdown pick land on the same rows.
  const [surveyRows, setSurveyRows] = useAtom(surveyRowsAtom);
  // Seeded and dropdown rows arrive without their tract polygon; fill it in so
  // every row grids like a map-picked one.
  useHydrateSurveyGeometries(surveyRows, setSurveyRows);
  // Snapping mirrors the composition: you can snap to whatever the boundary is
  // MADE OF — the unit's own footprint and the tracts picked onto it — and to
  // nothing else. Nothing is read out of the map.
  //
  // Quarter positions for those shapes: drawn as the guide, and offered as snap
  // targets.
  const quarterGrid = useMemo(
    () => quarterGridForUnit(surveyRows, baseRing),
    [surveyRows, baseRing]
  );
  // Real survey corners first, grid dots after: where both are within the snap
  // radius the true corner wins, so the grid stays a convenience and never
  // becomes the source of truth for the boundary.
  // The shapes a dragged vertex may land on, edges included.
  const snapRings = useMemo(
    () => outlineRingsFor(surveyRows, baseRing),
    [surveyRows, baseRing]
  );
  const snapTargets = useMemo(
    () => [...outlineVerticesFor(surveyRows, baseRing), ...quarterGrid],
    [surveyRows, baseRing, quarterGrid]
  );

  // Map-click survey selection, armed by the toolbar toggle. `pick` toggles a
  // clicked section in/out of the rows; `choices` surfaces an ambiguous overlap.
  // Rows are written through `editRows` so a pick is one undoable action.
  const [surveyPickArmed, setSurveyPickArmed] = useState(false);
  const editRows = useSurveyRowEdit();
  const surveyPick = useSurveyAreaPick(surveyRows, editRows);
  // Existing units can't gain survey areas, and `surveyPickArmed` is local state
  // that outlives a unit re-scope — so gate the VALUE rather than swapping the
  // handlers. `editRows` / `surveyPick.pick` must keep one identity per render or
  // `MapPickControl` detaches its listener and picking silently dies
  // (see use-survey-row-edit.ts).
  const pickArmed = surveyPickArmed && isDraftUnit;
  // The survey grid is empty when zoomed out, so arming zooms in to where
  // sections are pickable and the hint asks the user back in if they pull out.
  const pickTooFarOut = useMapZoomBelow(mapRef, SURVEY_PICK_ZOOM);
  useEffect(() => {
    if (!pickArmed || !mapRef) return;
    const map = mapRef.getMap();
    if (map.getZoom() < SURVEY_PICK_ZOOM)
      map.easeTo({ zoom: SURVEY_PICK_ZOOM });
  }, [pickArmed, mapRef]);
  const pickedIds = useMemo(
    () =>
      surveyRows
        .map((row) => row.location?.id)
        .filter((id): id is number => typeof id === 'number'),
    [surveyRows]
  );

  // Compose the unit boundary from the selected survey areas — the server's
  // authoritative union + acreage (also carves quarter calls), matching
  // /area_geometries. Idle (dev-area ring stays) until a tract is named.
  const filledRows = surveyRows.filter((row) => row.location);
  // `category` + `metadata` make this the SAME dry run the Create New Area panel
  // does, so it returns the server's gross acreage AND judges the name (a Unit's
  // routed development area validates `name` for uniqueness). Without them the
  // call only reshapes geometry. A draft with no tract picked yet still has a
  // boundary, so it falls back to a drawn source — that is the only way a clone's
  // rename gets validated. `drawn` returns no acreage, which the panel handles.
  const drawnGeojson = useMemo(
    () => piecesToGeojson(shapeEdit.pieces),
    [shapeEdit.pieces]
  );
  const previewSource = useMemo(() => {
    if (filledRows.length > 0) {
      return {
        type: 'survey' as const,
        rows: filledRows.map((row) => ({
          reference_location_id: row.location?.id,
          quarter_calls: row.quarterCalls,
        })),
      };
    }
    return isDraftUnit && drawnGeojson
      ? { type: 'drawn' as const, geojson: drawnGeojson }
      : null;
  }, [filledRows, isDraftUnit, drawnGeojson]);
  const preview = useAreaPreview(
    previewSource
      ? {
          source: previewSource,
          category: 'Unit',
          metadata: { horizontal_unit: true },
          // The effective name, so an untouched one is checked too. A clone's
          // auto "<base> (copy)" is only deduped against other session clones,
          // never against saved units, so without this it reached Save unchecked.
          ...(effectiveName ? { name: effectiveName } : {}),
        }
      : null
  );

  // Hand the dry run's verdict to the header, which owns the name field. One
  // preview serves both: acreage here, name validity there.
  const setNameCheck = useSetAtom(builderNameCheckAtom);
  useEffect(() => {
    setNameCheck({ loading: preview.loading, errors: preview.errors });
  }, [preview.loading, preview.errors, setNameCheck]);

  // A real reshape — vertex drag / insert / delete — arrives only through the
  // shape editor's `onChange`, so that is the one place the edit flag is set. The
  // survey-preview effect below writes `shapeEditRef.current.setPieces` directly
  // and therefore does NOT trip it, which is what lets Save tell "survey picked"
  // apart from "boundary reshaped".
  // Read as well as written here: it is both the provenance signal Save reads and
  // the mock's `freehandPolys !== null` — while it is set, the composition below
  // must not overwrite the shape the operator dragged.
  const [boundaryUserEdited, setBoundaryUserEdited] = useAtom(
    boundaryUserEditedAtom
  );
  const setPieces = shapeEdit.setPieces;
  const handleBoundaryUserChange = useCallback(
    (next: Pieces) => {
      setBoundaryUserEdited(true);
      setPieces(next);
    },
    [setBoundaryUserEdited, setPieces]
  );

  // The survey half of the boundary: the server's carved + unioned tract
  // geometry. Only when a tract is actually named — with none, `previewSource`
  // falls back to the DRAWN boundary, and feeding that back in would compose the
  // shape against itself.
  const surveyPieces = useMemo(() => {
    if (filledRows.length === 0 || !preview.geometry) return NO_PIECES;
    return geometryToPieces(preview.geometry);
  }, [filledRows.length, preview.geometry]);
  // The whole boundary: the unit's footprint UNIONED with those tracts — the
  // mock's `composeUnitPolys(baseRing, surveyAreas)`. This is the single
  // composition; the editable boundary, the setback ring and the save payload all
  // read it, so what is drawn, what is measured and what is sent agree. (They did
  // not before: this effect pushed the survey-only shape while the setback and the
  // payload each re-merged the unit in behind the operator's back.)
  const composedPieces = useMemo(
    () => composeUnitBoundary(baseRing, surveyPieces),
    [baseRing, surveyPieces]
  );

  // Push the composed shape into the editable boundary. A vertex drag then wins
  // outright until the next survey-row edit re-composes — the mock's
  // `freehandPolys ?? composeUnitPolys(...)`, where any row mutation clears the
  // freehand override. Read shapeEdit through a ref so this fires on the geometry,
  // not on every edit.
  const shapeEditRef = useRef(shapeEdit);
  shapeEditRef.current = shapeEdit;
  // No undo entry here: the edit that caused this composition already recorded
  // one, back when the rows were still the pre-edit set (`useSurveyRowEdit`).
  // Snapshotting at this point instead captured the post-edit rows, which is why
  // an undone removal used to restore the shape without the row.
  // DRAFTS ONLY. An existing unit's boundary is the true server polygon and must
  // never be replaced by a survey-composed shape — its rows are seeded from the
  // unit itself purely to show the description and measure acreage, so composing
  // from them here would silently redraw the saved unit.
  useEffect(() => {
    if (!isDraftUnit) return;
    if (boundaryUserEdited) return;
    if (composedPieces.length === 0) return;
    shapeEditRef.current.setPieces(composedPieces);
  }, [composedPieces, isDraftUnit, boundaryUserEdited]);

  // Open framed on the unit. The page map fits the whole type curve area, which
  // leaves the unit a speck — useless for vertex editing. Fits once per unit
  // (`framedUnitRef`) rather than on every boundary change, so a drag never yanks
  // the camera. Keyed on the boundary ring too: a real unit's ring is the server
  // polygon, which loads AFTER the map ref is ready, so this must fire when the
  // ring arrives — not only on mount (`originalRing` is stable per unit, so this
  // fits once on load, not on edits).
  const framedUnitRef = useRef<number | null>(null);
  useEffect(() => {
    if (!mapRef || !originalRing || originalRing.length === 0) return;
    const unitKey = selectedDevArea?.developmentAreaId ?? null;
    if (framedUnitRef.current === unitKey) return;
    const map = mapRef.getMap();
    const bounds = originalRing.reduce(
      (acc, [lng, lat]) => acc.extend([lng, lat] as [number, number]),
      new mapboxGl.LngLatBounds(
        originalRing[0] as [number, number],
        originalRing[0] as [number, number]
      )
    );
    // Framed tight to the ring, the unit filled the pane with no surrounding
    // sections to judge a reshape against, so this pulls back a little.
    const fit = () => {
      map.fitBounds(bounds, { padding: 90, maxZoom: 14 });
      framedUnitRef.current = unitKey;
    };

    // fitBounds before the style is up is dropped, and the builder's map mounts
    // fresh every time it opens, so wait for load when it is not ready yet.
    if (map.isStyleLoaded()) {
      fit();
      return undefined;
    }
    map.once('load', fit);
    return () => {
      map.off('load', fit);
    };
  }, [mapRef, selectedDevArea?.developmentAreaId, originalRing]);

  return (
    <div className={style.mapStack}>
      <div className={`${style.pane} ${style.paneFixed}`}>
        <UnitBuilderPaneHeader
          title="Map View"
          info="Drag a boundary vertex (◇) to reshape the unit freehand, click an edge midpoint to add one, right-click a vertex to delete it. To build the unit from survey areas, turn on Select survey area and click a landgrid tract on the map (or pick one from the dropdown below); quarter calls carve each tract — S2 = south half, S2S2 = south quarter, SW = SW corner, SWNW = NW of SW. The yellow dashed outline is the original unit; Save to persist. LTD laterals can be dragged to reposition; to trim one, scope its property first — click its marker on the gun barrel or tick its row in the table — then drag either end grip. Right-click a grip to restore that end to full length."
        />
        <UnitBuilderMapToolbar
          rotation={rotation}
          regenerating={regenerating}
          onCloneUnit={handleCloneUnit}
          isDraft={unitId === null}
          snap={snap}
          onSnapChange={setSnap}
          surroundings={surroundings}
          onSurroundingsChange={setSurroundings}
          grid={grid}
          onGridChange={setGrid}
          surveyPick={pickArmed}
          onSurveyPickChange={setSurveyPickArmed}
          surveyPickDisabled={!isDraftUnit}
        />
        <div
          className={style.mapCanvas}
          data-resize-target="--unit-builder-map-h"
        >
          {boundaryLoading && (
            <div className={style.slotsLoadingOverlay} role="status">
              <Loading
                small
                withOverlay={false}
                description="Loading unit boundary…"
              />
            </div>
          )}
          {!boundaryLoading && mapSlotsLoading && (
            <div className={style.slotsLoadingOverlay} role="status">
              <Loading small withOverlay={false} description="Loading slots…" />
            </div>
          )}
          {pickArmed && (
            <div className={style.surveyPickHint} role="status">
              {surveyPick.loading
                ? 'Reading survey area…'
                : (surveyPick.notice ??
                  (pickTooFarOut
                    ? 'Zoom in to select survey sections.'
                    : 'Click a survey section on the map to add it.'))}
            </div>
          )}
          {surveyPick.choices.length > 0 && (
            <div className={style.surveyPickChoices} role="dialog">
              <p>Multiple survey areas here — pick one:</p>
              {surveyPick.choices.map((candidate) => (
                <Button
                  key={candidate.id}
                  kind="ghost"
                  size="sm"
                  onClick={() => surveyPick.choose(candidate)}
                >
                  {describeSurveyAreaCandidate(candidate)}
                </Button>
              ))}
              <Button kind="ghost" size="sm" onClick={surveyPick.dismiss}>
                Cancel
              </Button>
            </div>
          )}
          <TypeCurveMap
            id={typeCurveId}
            hideUnitToolbar
            unitShapeEdit={{
              pieces: shapeEdit.pieces,
              onChange: handleBoundaryUserChange,
              onBeforeChange: shapeEdit.pushHistory,
              originalRing,
              snap,
              extraSnapTargets: snapTargets,
              snapRings,
            }}
            extraLayers={
              <>
                {/* The builder's own development-area layer set. Mounted FIRST
                    so it sits at the bottom of the extras, and the page map's
                    ordering effect drops these ids below the wells anyway.
                    `TcDevelopmentAreasLayer` still owns the source, the tile
                    query and the section line on this surface — it just leaves
                    the layers to this one. */}
                <UnitBuilderDevAreaLayers />
                {/* The grid exists to position the editable boundary, so
                    it goes when that boundary does — "Type Curve Units" hides
                    the unit, and a field of dots with nothing to snap is just
                    noise. The toolbar toggle still owns it independently. */}
                {grid && unitsVisible ? (
                  <QuarterGridLayer points={quarterGrid} />
                ) : null}
                {/* This unit's LTD laterals, fetched unit-scoped (not the
                    workspace-wide layer). Null for a draft unit (no LTDs yet). */}
                {unitId !== null ? (
                  <TcSlotGeometriesLayer
                    unitId={unitId}
                    slotsAtom={mapSlotGeomAtoms.builder}
                    formationNameOverride={builderFormation?.formation ?? null}
                    packageIdOverride={
                      builderFormation?.inventoryFormationPackageId ?? null
                    }
                  />
                ) : null}
                {/* Trim grips for the selected LTD lateral; renders nothing
                    unless a single valued LTD is scoped. */}
                {unitId !== null ? <TcLateralTrimLayer /> : null}
                {unitId !== null ? <MergeGeometriesLayer /> : null}
                {/* Cross-line / In-line setback keep-out ring, live from the
                    spacing inputs; renders nothing when both are 0. */}
                {/* The ring follows the bearing the LTDs are actually at, so a
                    rotate turns it with the section rather than leaving it on
                    the tile's bearing until a reload. */}
                <TcSetbackRingLayer
                  pieces={shapeEdit.pieces}
                  azimuthDeg={rotation.effectiveAzimuthDeg}
                />
                {/* Pickable survey grid — always shows the selected sections;
                    the faint clickable wash only when picking is armed. */}
                {/* `showPickedFill` off: a picked area is already part of the
                    unit boundary, which paints its own fill. Painting it twice
                    made the survey half of a unit read darker than the rest. */}
                <SurveyAreasLayer
                  pickedIds={pickedIds}
                  showFill={pickArmed}
                  showPickedFill={false}
                />
                {/* Click → candidates; only picks while armed. */}
                <MapPickControl
                  onPickSurveyArea={pickArmed ? surveyPick.pick : null}
                />
              </>
            }
          />
        </div>
      </div>
      <UnitBuilderRowResizer
        cssVar="--unit-builder-map-h"
        min={160}
        label="Resize map"
      />
      <UnitBuilderSurveyAreas grossAcres={preview.grossAcres} />
    </div>
  );
};
